import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import classNames from 'classnames'
import { PasswordInput } from 'components/password-input'
import { Button } from 'components/ui/button'
import { Card } from 'components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'components/ui/form'
import { Input } from 'components/ui/input'
import AuthLayout from 'features/auth/auth-layout'
import { loginAsync } from 'store/authentication/authSlice'
import { useAppDispatch, useAppSelector } from 'store/store'

const formSchema = z.object({
  email: z
    .string()
    .min(1, { message: 'Please enter your email' })
    .email({ message: 'Invalid email address' }),
  password: z
    .string()
    .min(1, {
      message: 'Please enter your password',
    })
    .min(7, {
      message: 'Password must be at least 7 characters long',
    }),
})

const LoginPage = () => {
  const dispatch = useAppDispatch()
  const { loading, error } = useAppSelector((state) => state.authentication)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  function onSubmit(data: z.infer<typeof formSchema>) {
    dispatch(loginAsync(data))
  }

  return (
    <AuthLayout>
      <Card className='p-6'>
        <div className='flex flex-col space-y-2 text-left'>
          <h1 className='text-2xl font-semibold tracking-tight'>Login</h1>
          {error ? (
            <p className='text-red text-sm'>{error}</p>
          ) : (
            <p className='text-sm text-muted-foreground'>
              Inserisci email e password <br />
              per accedere al tuo account.
            </p>
          )}
        </div>
        <div className={classNames('grid gap-6')}>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className='grid gap-2'>
                <FormField
                  control={form.control}
                  name='email'
                  render={({ field }) => (
                    <FormItem className='space-y-1'>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input placeholder='name@example.com' {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='password'
                  render={({ field }) => (
                    <FormItem className='space-y-1'>
                      <div className='flex items-center justify-between'>
                        <FormLabel>Password</FormLabel>
                      </div>
                      <FormControl>
                        <PasswordInput placeholder='********' {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button className='mt-2' disabled={loading}>
                  Login
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </Card>
    </AuthLayout>
  )
}

export default LoginPage
